import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import arTranslations from "./locales/ar.json";
import enTranslations from "./locales/en.json";
import frTranslations from "./locales/fr.json";

export enum LanguageEnum {
  EN = "en",
  FR = "fr",
  AR = "ar",
}

const isDevelopment = process.env.NODE_ENV === "development";
const SUPPORTED_LANGUAGES = ["en", "fr", "ar"];
const RTL_LANGUAGES = ["ar"];
const STORAGE_KEY = "i18nextLng";

export const setDocumentDir = (lng: string) => {
  const dir = RTL_LANGUAGES.includes(lng) ? "rtl" : "ltr";
  document.documentElement.dir = dir;
  document.documentElement.lang = lng;
  if (isDevelopment) {
    console.log(`setDocumentDir: lng=${lng} dir=${dir}`);
  }
};

const getInitialLanguage = () => {
  try {
    const storedLang = localStorage.getItem(STORAGE_KEY);
    if (storedLang && SUPPORTED_LANGUAGES.includes(storedLang)) {
      if (isDevelopment) console.log("Language from localStorage:", storedLang);
      return storedLang;
    }

    const browserLang = navigator.language.split("-")[0];
    if (SUPPORTED_LANGUAGES.includes(browserLang)) {
      if (isDevelopment) console.log("Language from browser:", browserLang);
      localStorage.setItem(STORAGE_KEY, browserLang);
      return browserLang;
    }

    const htmlLang = document.documentElement.lang?.split("-")[0];
    if (htmlLang && SUPPORTED_LANGUAGES.includes(htmlLang)) {
      if (isDevelopment) console.log("Language from HTML:", htmlLang);
      localStorage.setItem(STORAGE_KEY, htmlLang);
      return htmlLang;
    }

    if (isDevelopment) console.log("Using fallback language: en");
    localStorage.setItem(STORAGE_KEY, "en");
    return "en";
  } catch (error) {
    console.warn("Error detecting language, using fallback:", error);
    return "en";
  }
};

const resources = {
  en: { translation: enTranslations },
  fr: { translation: frTranslations },
  ar: { translation: arTranslations },
};

if (!i18n.isInitialized) {
  const initialLanguage = getInitialLanguage();

  // Set immediately before anything renders
  setDocumentDir(initialLanguage);

  // Register listener BEFORE init so it catches the initial languageChanged event
  i18n.on("languageChanged", (lng) => {
    localStorage.setItem(STORAGE_KEY, lng);
    setDocumentDir(lng);
    if (isDevelopment) console.log("Language changed to:", lng);
  });

  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      lng: initialLanguage,
      fallbackLng: "en",
      supportedLngs: SUPPORTED_LANGUAGES,
      debug: isDevelopment,

      detection: {
        order: ["localStorage", "navigator", "htmlTag"],
        caches: ["localStorage"],
        lookupLocalStorage: STORAGE_KEY,
        convertDetectedLanguage: (lng) => {
          const cleanLng = lng.split("-")[0];
          return SUPPORTED_LANGUAGES.includes(cleanLng) ? cleanLng : "en";
        },
      },

      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      keySeparator: ".",
      load: "languageOnly",

      missingKeyHandler: (
        lngs: readonly string[],
        ns: string,
        key: string,
        _fallbackValue: string,
        _updateMissing: boolean,
        _options: object,
      ) => {
        if (isDevelopment) {
          console.warn(
            `Missing translation key: "${key}" for languages: [${lngs.join(", ")}] (ns: ${ns})`,
          );
        }
      },

      parseMissingKeyHandler: (key: string) => (isDevelopment ? `⚠️ ${key}` : key),
    });
}

export default i18n;
export const t = i18n.t.bind(i18n);
